import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import FilterFooter from '~/client/src/desktop/components/Filters/FilterFooter/FilterFooter'
import FilterHeader from '~/client/src/desktop/components/Filters/FilterHeader/FilterHeader'
import FilterOption from '~/client/src/shared/components/Filters/FilterUtils/FilterOption/FilterOption'
import { Loader } from '~/client/src/shared/components/Loader'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import SuperFilterStore from '~/client/src/shared/stores/ui/SuperFilter.store'

interface ISuperFilter {
  filterStore: SuperFilterStore
  isClearAllShown?: boolean

  hideOtherModalsFn?(): void
}

@observer
export default class SuperFilter extends React.Component<ISuperFilter> {
  @observable private localProps: ISuperFilter = null
  public constructor(props: ISuperFilter) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  private get message() {
    const { filterCaption } = this.store
    return 'You have to configure filter ' + filterCaption
  }

  @action
  public componentDidUpdate(): void {
    if (this.localProps.isClearAllShown !== this.props.isClearAllShown)
      this.localProps.isClearAllShown = this.props.isClearAllShown
    if (this.props.filterStore !== this.store)
      this.localProps.filterStore = this.props.filterStore
  }

  public componentDidMount() {
    this.localProps.filterStore.clearNotPresentOptions()
  }

  public render() {
    const { isShown, isLoading, isEmpty } = this.store

    return (
      isShown && (
        <div
          className={classList({
            'filter-container location tw:left-0 tw:top-[calc(100%+10px)] tw:max-[1100px]:left-2.5': true,
            'short row': isLoading || isEmpty,
          })}
        >
          {isLoading ? <Loader /> : this.renderFilterArea()}
        </div>
      )
    )
  }

  private get store(): SuperFilterStore {
    return this.localProps.filterStore
  }

  private get notification() {
    return <div className="filter-notification center">{this.message}</div>
  }

  @computed
  private get filter() {
    const {
      clickOnDeselectAll,
      clickOnSelectAll,
      isDisabled,
      applyButtonLabel,
      shortNameCaption,
      nameCaption,
      totalHint,
      shouldHideCount,
    } = this.store

    const className = 'bb-light-grey' + (isDisabled ? ' inactive-element' : '')
    return (
      <>
        <FilterHeader
          clickOnSelectAll={clickOnSelectAll}
          clickOnClearAll={
            this.localProps.isClearAllShown && clickOnDeselectAll
          }
          className={className}
        >
          <div className="row px10 py10">
            {shortNameCaption && (
              <div className="filter-header-hint code">{shortNameCaption}</div>
            )}
            <div className="filter-header-hint desk">{nameCaption}</div>
            {!shouldHideCount && (
              <div className="filter-header-hint act">
                <span className="text large primary-blue">Viewable</span>
                <span className="ml5 mr5">/</span>
                {totalHint}
              </div>
            )}
          </div>
        </FilterHeader>

        <div className={'filter-content pt10 pb10 ' + className}>
          {this.renderOptionsTree()}
        </div>

        <FilterFooter
          clickOnCancel={this.onCancelClick}
          clickOnApply={this.onApplyClick}
          applyButtonText={applyButtonLabel}
        />
      </>
    )
  }

  private renderFilterArea() {
    return !this.store.isEmpty ? this.filter : this.notification
  }

  private renderOptionsTree() {
    const {
      optionsTree,
      changeRootOption,
      isRootOptionChecked,
      selectedOptions,
      changeOption,
      clickOnOnly,
      shouldHideCount,
    } = this.store

    const showRootOptions = this.store.filterSourceType === 'ProjectMember'

    return optionsTree.map(rootOption => {
      const options = rootOption.options.map((option, index) => {
        const { id, shortName, name, instancesIds } = option
        return (
          <FilterOption
            key={index}
            id={id}
            code={shortName}
            description={name}
            instancesIds={instancesIds}
            selectedOptions={selectedOptions}
            changeOption={changeOption}
            onOnlyClicked={clickOnOnly}
            croppedAmount={this.store.getCroppedAmount(option)}
            totalAmount={this.store.getTotalAmount(option)}
            shouldIgnoreCount={shouldHideCount}
          />
        )
      })

      return (
        <div className="filter-section" key={rootOption.id}>
          {showRootOptions && (
            <div className="filter-section-title">
              <label className="tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:select-none">
                <Checkbox
                  checked={isRootOptionChecked(rootOption.options)}
                  onCheckedChange={() => changeRootOption(rootOption.options)}
                />
                <span>{rootOption.name}</span>
              </label>
            </div>
          )}
          {options}
        </div>
      )
    })
  }

  private onCancelClick = () => {
    this.store.clickOnCancel()
    this.localProps.hideOtherModalsFn?.()
  }

  private onApplyClick = () => {
    this.store.clickOnApply()
    this.localProps.hideOtherModalsFn?.()
  }
}
